import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Flex,
  SimpleGrid,
  ScaleFade,
  Heading,
  Text,
  Spinner,
  Icon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Progress,
  Badge,
  useColorModeValue,
} from '@chakra-ui/react';
import { IconType } from 'react-icons';
import {
  FiTruck,
  FiActivity,
  FiTrendingUp,
  FiDollarSign,
  FiTool,
  FiClock,
} from 'react-icons/fi';
// eslint-disable-next-line import/no-duplicates
import { format, startOfMonth, endOfMonth, subMonths } from 'date-fns';
// eslint-disable-next-line import/no-duplicates
import ptBR from 'date-fns/locale/pt-BR';

import { HeaderUp } from '../../../../components/_components_0.2/Header_0.2';
import { Sidebar } from '../../../../components/_components_0.2/Sidebar_0.2';
import { api } from '../../../../services/api';

interface IVehicle {
  id: string;
  name: string;
  plate: string;
  fuel: string;
  km: string | number;
  status: boolean;
}

interface IAppointment {
  id: string;
  status: string;
}

interface IMonthSeries {
  months: string[];
  results: number[];
}

const brl = (value: number): string =>
  value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const kmFmt = (value: number): string =>
  `${Math.round(value).toLocaleString('pt-BR')} km`;

// ---------------------------------------------------------------------------
// Componentes de apresentação (padrão visual SAMASC)
// ---------------------------------------------------------------------------

const SurfaceCard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const shadow = useColorModeValue('base', 'dark-lg');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  return (
    <Box
      bg="gray.800"
      borderWidth="1px"
      borderColor={borderColor}
      boxShadow={shadow}
      borderRadius="lg"
      p="6"
    >
      {children}
    </Box>
  );
};

interface StatCardProps {
  icon: IconType;
  label: string;
  value: React.ReactNode;
  help?: string;
  accent: string;
}

const StatCard: React.FC<StatCardProps> = ({
  icon,
  label,
  value,
  help,
  accent,
}) => (
  <SurfaceCard>
    <Flex align="center" mb="3">
      <Flex
        align="center"
        justify="center"
        w="10"
        h="10"
        borderRadius="lg"
        bg={`${accent}.100`}
        color={`${accent}.500`}
        mr="3"
      >
        <Icon as={icon} fontSize="20" />
      </Flex>
      <Text fontSize="sm" color="gray.500" fontWeight="500">
        {label}
      </Text>
    </Flex>
    <Text fontSize="3xl" fontWeight="700" color="gray.50" lineHeight="1.1">
      {value}
    </Text>
    {help ? (
      <Text fontSize="xs" color="gray.500" mt="1">
        {help}
      </Text>
    ) : null}
  </SurfaceCard>
);

const MiniBarChart: React.FC<{ labels: string[]; values: number[] }> = ({
  labels,
  values,
}) => {
  const max = Math.max(...values, 1);
  const empty = values.length === 0 || values.every(value => value === 0);

  if (empty) {
    return (
      <Flex h="200px" align="center" justify="center">
        <Text color="gray.500">Sem dados de quilometragem no período.</Text>
      </Flex>
    );
  }

  return (
    <Flex align="flex-end" justify="space-between" h="200px" gap="3" px="1">
      {values.map((value, index) => (
        <Flex
          // eslint-disable-next-line react/no-array-index-key
          key={`${labels[index]}-${index}`}
          direction="column"
          align="center"
          justify="flex-end"
          flex="1"
          h="100%"
        >
          <Text fontSize="xs" color="gray.500" mb="1" fontWeight="500">
            {value > 0 ? Math.round(value).toLocaleString('pt-BR') : ''}
          </Text>
          <Box
            w="100%"
            maxW="48px"
            bg="yellow.500"
            borderTopRadius="md"
            h={`${(value / max) * 100}%`}
            minH={value > 0 ? '6px' : '0'}
            transition="height 0.5s ease"
          />
          <Text
            fontSize="xs"
            color="gray.500"
            mt="2"
            textTransform="capitalize"
          >
            {labels[index]}
          </Text>
        </Flex>
      ))}
    </Flex>
  );
};

// ---------------------------------------------------------------------------
// Página
// ---------------------------------------------------------------------------

export default function Dashboard() {
  const [vehicles, setVehicles] = useState<IVehicle[]>([]);
  const [appointments, setAppointments] = useState<IAppointment[]>([]);
  const [kmSeries, setKmSeries] = useState<IMonthSeries>({
    months: [],
    results: [],
  });
  const [consumption, setConsumption] = useState(0);
  const [fuelCost, setFuelCost] = useState(0);
  const [maintenanceCost, setMaintenanceCost] = useState(0);
  const [loading, setLoading] = useState(true);

  const now = useMemo(() => new Date(), []);
  const monthLabel = format(now, "MMMM 'de' yyyy", { locale: ptBR });

  useEffect(() => {
    let active = true;

    // Soma amount_total dos registros cujo mês/ano batem com o mês atual.
    const sumThisMonth = (
      list: Array<{ date?: string; amount_total?: string | number }>,
    ): number => {
      if (!Array.isArray(list)) return 0;
      return list.reduce((total, item) => {
        if (!item?.date) return total;
        // "T00:00:00" força horário local — sem isso, datas tipo "2026-08-01"
        // viram meia-noite UTC (31/07 21h no Brasil) e o dia 1 de cada mês
        // seria contado no mês anterior.
        const date = new Date(`${item.date}T00:00:00`);
        if (
          date.getMonth() === now.getMonth() &&
          date.getFullYear() === now.getFullYear()
        ) {
          return total + (Number(item.amount_total) || 0);
        }
        return total;
      }, 0);
    };

    async function load() {
      setLoading(true);

      const monthParam = `${now.getMonth() + 1 < 10 ? '0' : ''}${
        now.getMonth() + 1
      }-${now.getFullYear()}`;
      const monthStart = format(startOfMonth(now), 'yyyy-MM-dd');
      const monthEnd = format(endOfMonth(now), 'yyyy-MM-dd');
      const sixMonthsStart = format(
        startOfMonth(subMonths(now, 5)),
        'yyyy-MM-dd',
      );

      const [vehiclesRaw, apptRaw, kmRaw, fuelRaw, supRaw, maintRaw] =
        await Promise.all([
          api
            .get('/vehicles')
            .then(r => r.data)
            .catch(() => []),
          api
            .get('/appointments/filter', {
              params: { month: monthParam, take: 999, page: 1 },
            })
            .then(r => r.data)
            .catch(() => ({ appointment: [] })),
          api
            .get('/kilometers/calc-km-traveled-month', {
              params: { start_date: sixMonthsStart, end_date: monthEnd },
            })
            .then(r => r.data)
            .catch(() => ({ months: [], results: [] })),
          api
            .get('/supplies/calc-fuel-month', {
              params: { start_date: monthStart, end_date: monthEnd },
            })
            .then(r => r.data)
            .catch(() => ({ months: [], results: [] })),
          api
            .get('/supplies/filter', { params: { take: 9999, page: 1 } })
            .then(r => r.data)
            .catch(() => ({})),
          api
            .get('/maintenance/filter', { params: { take: 9999, page: 1 } })
            .then(r => r.data)
            .catch(() => ({ maintenance: [] })),
        ]);

      if (!active) return;

      setVehicles(Array.isArray(vehiclesRaw) ? vehiclesRaw : []);

      setAppointments(
        Array.isArray(apptRaw?.appointment) ? apptRaw.appointment : [],
      );

      setKmSeries({
        months: Array.isArray(kmRaw?.months) ? kmRaw.months : [],
        results: Array.isArray(kmRaw?.results)
          ? kmRaw.results.map((n: unknown) => Number(n) || 0)
          : [],
      });

      const fuelResults: number[] = Array.isArray(fuelRaw?.results)
        ? fuelRaw.results
        : [];
      setConsumption(
        fuelResults.length
          ? Number(fuelResults[fuelResults.length - 1]) || 0
          : 0,
      );

      const suppliesList =
        supRaw?.supplies ??
        supRaw?.supply ??
        (Array.isArray(supRaw) ? supRaw : []);
      setFuelCost(sumThisMonth(suppliesList));

      const maintenanceList = Array.isArray(maintRaw?.maintenance)
        ? maintRaw.maintenance
        : [];
      setMaintenanceCost(sumThisMonth(maintenanceList));

      setLoading(false);
    }

    load();

    return () => {
      active = false;
    };
  }, [now]);

  const activeVehicles = useMemo(
    () => vehicles.filter(vehicle => vehicle.status !== false),
    [vehicles],
  );
  const inactiveVehicles = useMemo(
    () => vehicles.filter(vehicle => vehicle.status === false),
    [vehicles],
  );

  const kmThisMonth = useMemo(
    () =>
      kmSeries.results.length
        ? kmSeries.results[kmSeries.results.length - 1]
        : 0,
    [kmSeries],
  );

  const countByStatus = useMemo(() => {
    const counts = { Pendente: 0, Aprovado: 0, Recusado: 0 };
    appointments.forEach(appointment => {
      if (appointment.status in counts) {
        counts[appointment.status as keyof typeof counts] += 1;
      }
    });
    return counts;
  }, [appointments]);

  const totalAppointments = appointments.length;

  const statusRows = [
    { label: 'Pendentes', value: countByStatus.Pendente, color: 'yellow' },
    { label: 'Aprovados', value: countByStatus.Aprovado, color: 'green' },
    { label: 'Recusados', value: countByStatus.Recusado, color: 'red' },
  ];

  const fuelDistribution = useMemo(() => {
    const map: Record<string, number> = {};
    activeVehicles.forEach(vehicle => {
      const fuel = vehicle.fuel || 'Não informado';
      map[fuel] = (map[fuel] || 0) + 1;
    });
    return Object.entries(map)
      .map(([label, count]) => ({ label, count }))
      .sort((a, b) => b.count - a.count);
  }, [activeVehicles]);

  const vehiclesByKm = useMemo(
    () =>
      [...activeVehicles]
        .sort((a, b) => (Number(b.km) || 0) - (Number(a.km) || 0))
        .slice(0, 8),
    [activeVehicles],
  );

  return (
    <ScaleFade initialScale={0.95} in>
      <Flex direction="column" minH="100vh">
        <HeaderUp path="miles" />
        <Flex w="100%" my="6" maxWidth={1480} mx="auto" pb={8} px="6">
          <Sidebar path="miles" />
          <Box flex="1">
            <Flex align="center" mb="1">
              <Box
                w="4px"
                h="28px"
                bg="yellow.500"
                borderRadius="full"
                mr="3"
              />
              <Heading size="lg" color="gray.50">
                Painel da Frota
              </Heading>
            </Flex>
            <Text color="gray.500" mb="6" textTransform="capitalize" ml="4">
              {monthLabel}
            </Text>

            {loading ? (
              <Flex justify="center" py="24">
                <Spinner size="xl" color="yellow.500" thickness="4px" />
              </Flex>
            ) : (
              <>
                <SimpleGrid columns={[1, 2, 3, 6]} spacing="5" mb="6">
                  <StatCard
                    icon={FiTruck}
                    accent="yellow"
                    label="Veículos ativos"
                    value={activeVehicles.length}
                    help={`${inactiveVehicles.length} inativo(s)`}
                  />
                  <StatCard
                    icon={FiActivity}
                    accent="blue"
                    label="KM rodado no mês"
                    value={kmFmt(kmThisMonth)}
                    help="Soma da quilometragem"
                  />
                  <StatCard
                    icon={FiTrendingUp}
                    accent="teal"
                    label="Consumo médio"
                    value={`${consumption.toLocaleString('pt-BR', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })} km/l`}
                    help="No mês atual"
                  />
                  <StatCard
                    icon={FiDollarSign}
                    accent="green"
                    label="Combustível (mês)"
                    value={brl(fuelCost)}
                    help="Total abastecido"
                  />
                  <StatCard
                    icon={FiTool}
                    accent="orange"
                    label="Manutenção (mês)"
                    value={brl(maintenanceCost)}
                    help="Total gasto"
                  />
                  <StatCard
                    icon={FiClock}
                    accent="purple"
                    label="Agend. pendentes"
                    value={countByStatus.Pendente}
                    help={`${totalAppointments} no mês`}
                  />
                </SimpleGrid>

                <SimpleGrid columns={[1, 1, 2]} spacing="6" mb="6">
                  <SurfaceCard>
                    <Heading size="md" fontWeight="500" color="gray.50" mb="1">
                      Quilometragem rodada
                    </Heading>
                    <Text fontSize="sm" color="gray.500" mb="5">
                      Últimos 6 meses
                    </Text>
                    <MiniBarChart
                      labels={kmSeries.months}
                      values={kmSeries.results}
                    />
                  </SurfaceCard>

                  <SurfaceCard>
                    <Heading size="md" fontWeight="500" color="gray.50" mb="1">
                      Agendamentos por status
                    </Heading>
                    <Text fontSize="sm" color="gray.500" mb="5">
                      {monthLabel}
                    </Text>
                    {totalAppointments === 0 ? (
                      <Flex h="150px" align="center" justify="center">
                        <Text color="gray.500">
                          Nenhum agendamento neste mês.
                        </Text>
                      </Flex>
                    ) : (
                      statusRows.map(row => (
                        <Box key={row.label} mb="5">
                          <Flex justify="space-between" mb="2">
                            <Text color="gray.500">{row.label}</Text>
                            <Text fontWeight="700" color="gray.50">
                              {row.value}
                            </Text>
                          </Flex>
                          <Progress
                            value={(row.value / totalAppointments) * 100}
                            colorScheme={row.color}
                            borderRadius="full"
                            size="sm"
                          />
                        </Box>
                      ))
                    )}
                  </SurfaceCard>
                </SimpleGrid>

                <SimpleGrid columns={[1, 1, 2]} spacing="6">
                  <SurfaceCard>
                    <Heading size="md" fontWeight="500" color="gray.50" mb="5">
                      Frota por combustível
                    </Heading>
                    {fuelDistribution.length === 0 ? (
                      <Text color="gray.500">Nenhum veículo ativo.</Text>
                    ) : (
                      fuelDistribution.map(item => (
                        <Box key={item.label} mb="4">
                          <Flex justify="space-between" mb="2">
                            <Text color="gray.500">{item.label}</Text>
                            <Text fontWeight="700" color="gray.50">
                              {item.count}
                            </Text>
                          </Flex>
                          <Progress
                            value={
                              (item.count / (activeVehicles.length || 1)) * 100
                            }
                            colorScheme="yellow"
                            borderRadius="full"
                            size="sm"
                          />
                        </Box>
                      ))
                    )}
                  </SurfaceCard>

                  <SurfaceCard>
                    <Heading size="md" fontWeight="500" color="gray.50" mb="5">
                      Veículos por quilometragem
                    </Heading>
                    <Box overflowX="auto">
                      {vehiclesByKm.length === 0 ? (
                        <Text color="gray.500">
                          Nenhum veículo ativo cadastrado.
                        </Text>
                      ) : (
                        <Table variant="simple" size="sm">
                          <Thead>
                            <Tr>
                              <Th>Veículo</Th>
                              <Th>Placa</Th>
                              <Th>Combustível</Th>
                              <Th isNumeric>KM atual</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {vehiclesByKm.map(vehicle => (
                              <Tr key={vehicle.id}>
                                <Td color="gray.50">{vehicle.name}</Td>
                                <Td>
                                  <Badge colorScheme="gray">
                                    {vehicle.plate}
                                  </Badge>
                                </Td>
                                <Td color="gray.500">{vehicle.fuel}</Td>
                                <Td isNumeric color="gray.50" fontWeight="500">
                                  {(Number(vehicle.km) || 0).toLocaleString(
                                    'pt-BR',
                                  )}
                                </Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      )}
                    </Box>
                  </SurfaceCard>
                </SimpleGrid>
              </>
            )}
          </Box>
        </Flex>
      </Flex>
    </ScaleFade>
  );
}
